/**
 * @module hooks/useHierarchyTree
 * @summary Phase 2 read hook for the aggregated hierarchy tree.
 * @description Fetches `GET /api/projects/:engagementId/hierarchy` via
 *   `lib/rpdm/hierarchy.readEngagementHierarchy`, holds the response in
 *   local state, and re-fetches on refresh-bus events for the
 *   engagement.
 *
 * ## Purpose
 * Give the Phase 2 Delivery Hierarchy page a single seam onto the
 * additive read endpoint. The hook shape is byte-stable across phases:
 *   `{ data, loading, error, refetch }`.
 *
 * ## Invariants (Phase 2)
 * - No network request when `engagementId` is null / undefined /
 *   empty. The hook returns `{data:null, loading:false, error:null}`
 *   in that case, matching Phase 1a behaviour.
 * - Refresh-bus subscription: `engagement:{id}` triggers a re-fetch.
 *   Unsubscribed on unmount (§ 11.0-C).
 * - The hook NEVER falls back to a legacy client-side join. On
 *   `capability_missing` (424) or `feature_disabled_404` from the
 *   backend, the error is surfaced as-is with `code` set — the caller
 *   renders `<FailClosedPlaceholder>`.
 * - `refetch` is a stable identity across renders (via `useCallback`).
 *
 * ## Behaviour under errors
 * `error` shape:
 *   `{ code: string, message?: string, raw?: any }`
 * Error `code` is one of:
 *   * `capability_missing` — additive endpoint returned 424
 *     (`{code:'capability_missing'}`) or 404
 *     (`{code:'feature_disabled_404'}` or code=`not_found`).
 *   * `permission_denied_403` — HTTP 403.
 *   * `engagement_not_found` — HTTP 404 with plain string detail.
 *   * `engagement_hierarchy_too_large` — HTTP 413 envelope refusal.
 *   * `refusal_unknown` — everything else.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { readEngagementHierarchy } from "../lib/rpdm/hierarchy";
import { subscribe as refreshBusSubscribe } from "../lib/rpdm/refreshBus";

function extractErrorCode(err) {
    if (!err) return "refusal_unknown";
    // 1. Explicit refusal thrown from lib (e.g. capabilities.assertAvailable).
    //    These carry `code` at the top level and are our own contract shape.
    if (err.code && typeof err.code === "string" && err.code.startsWith("capability")) {
        return err.code;
    }
    // 2. Backend response with structured detail.
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    if (detail && typeof detail === "object" && detail.code) return detail.code;
    if (status === 403) return "permission_denied_403";
    if (status === 404) {
        if (typeof detail === "string") {
            if (detail === "engagement_not_found") return "engagement_not_found";
            return "engagement_not_found";
        }
        return "capability_missing";
    }
    if (status === 413) return "engagement_hierarchy_too_large";
    if (status === 424) return "capability_missing";
    return "refusal_unknown";
}

/**
 * @param {string | null | undefined} engagementId - engagement scope.
 * @returns {{data: any, loading: boolean, error: any, refetch: () => void}}
 * @invariant Never issues a network request when engagementId is falsy.
 * @invariant Return shape is byte-stable across phases.
 */
export function useHierarchyTree(engagementId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(!!engagementId);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const generationRef = useRef(0);

    const fetchOnce = useCallback(async (id) => {
        if (!id) {
            setData(null);
            setLoading(false);
            setError(null);
            return;
        }
        const myGen = generationRef.current + 1;
        generationRef.current = myGen;
        if (abortRef.current) {
            try { abortRef.current.abort(); } catch (_e) { /* ignore */ }
        }
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setError(null);
        try {
            const payload = await readEngagementHierarchy(id, { signal: controller.signal });
            if (generationRef.current !== myGen) return;
            setData(payload);
            setError(null);
        } catch (err) {
            if (generationRef.current !== myGen) return;
            if (err && (err.name === "CanceledError" || err.name === "AbortError")) return;
            setData(null);
            setError({
                code: extractErrorCode(err),
                message: err?.message || String(err),
                raw: err,
            });
        } finally {
            if (generationRef.current === myGen) {
                setLoading(false);
            }
        }
    }, []);

    // Initial + on-engagement-change fetch.
    useEffect(() => {
        fetchOnce(engagementId);
        return () => {
            if (abortRef.current) {
                try { abortRef.current.abort(); } catch (_e) { /* ignore */ }
            }
        };
    }, [engagementId, fetchOnce]);

    // Refresh-bus subscription: re-fetch when this engagement is invalidated.
    useEffect(() => {
        if (!engagementId) return undefined;
        const unsubscribe = refreshBusSubscribe(
            [`engagement:${String(engagementId).toLowerCase()}`],
            () => { fetchOnce(engagementId); },
        );
        return unsubscribe;
    }, [engagementId, fetchOnce]);

    const refetch = useCallback(() => {
        fetchOnce(engagementId);
    }, [engagementId, fetchOnce]);

    return { data, loading, error, refetch };
}
